import { ArrowLeft, LoaderCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import FormularioGasto from '../components/gastos/FormularioGasto'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useAuth } from '../context/AuthContext'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { cargarFacturasPorGasto } from '../services/abastecimiento'
import { puedeGestionarGasto } from '../utils/permisosGastos'

const CAMPOS_GASTO = `
  id,
  empresa_id,
  categoria_id,
  proveedor_id,
  fecha_gasto,
  fecha_vencimiento,
  fecha_pago,
  tipo_documento,
  numero_documento,
  descripcion,
  monto_neto,
  monto_exento,
  monto_iva,
  otros_impuestos,
  monto_total,
  medio_pago,
  estado_pago,
  estado_registro,
  observaciones,
  creado_por,
  creado_en,
  actualizado_en,
  categoria:categorias_gasto!fk_gasto_categoria_empresa (
    id,
    nombre
  )
`

function EditarGasto() {
  const { gastoId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const [gasto, setGasto] = useState(null)
  const [facturaVinculada, setFacturaVinculada] = useState(null)
  const [cargandoGasto, setCargandoGasto] = useState(true)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    cargarGasto()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gastoId, empresaActual?.id])

  const cargarGasto = async () => {
    if (!gastoId || !empresaActual?.id) return

    try {
      setCargandoGasto(true)

      const { data, error: errorConsulta } = await supabase
        .from('gastos')
        .select(CAMPOS_GASTO)
        .eq('id', gastoId)
        .eq('empresa_id', empresaActual.id)
        .neq('estado_registro', 'anulado')
        .maybeSingle()

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      if (!data) {
        setGasto(null)

        abrirModal({
          tipo: 'error',
          titulo: 'Gasto no encontrado',
          mensaje:
            'El gasto no existe, fue eliminado o no pertenece a esta empresa.',
          onAceptar: () => {
            cerrarModal()
            navigate('/gastos')
          },
        })
        return
      }

      // Solo el creador o el administrador, y solo si su rol puede manejar
      // ese tipo de documento y categoría (misma regla que la RLS).
      if (!puedeGestionarGasto(empresaActual.rol, data, user?.id)) {
        setGasto(null)

        abrirModal({
          tipo: 'error',
          titulo: 'Sin permisos',
          mensaje:
            'Tu perfil no puede modificar este gasto. Puedes consultarlo desde el listado de Gastos.',
          onAceptar: () => {
            cerrarModal()
            navigate('/gastos')
          },
        })
        return
      }

      const [factura] = await cargarFacturasPorGasto(
        empresaActual.id,
        [data.id]
      )

      if (!montadoRef.current) return

      setFacturaVinculada(factura || null)
      setGasto(data)
    } catch (errorCarga) {
      console.error('Error al cargar gasto:', errorCarga)

      if (!montadoRef.current) return

      setGasto(null)

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar el gasto',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar el gasto.',
        textoAceptar: 'Reintentar',
        textoCancelar: 'Volver',
        mostrarCancelar: true,
        onAceptar: () => {
          cerrarModal()
          cargarGasto()
        },
        onCancelar: () => {
          cerrarModal()
          navigate('/gastos')
        },
      })
    } finally {
      if (montadoRef.current) {
        setCargandoGasto(false)
      }
    }
  }

  const guardarEdicion = async (datosFormulario) => {
    const { error: errorActualizar } = await supabase
      .from('gastos')
      .update(datosFormulario)
      .eq('id', gastoId)
      .eq('empresa_id', empresaActual.id)
      .neq('estado_registro', 'anulado')
      .select(CAMPOS_GASTO)
      .single()

    if (errorActualizar) {
      if (errorActualizar.code === 'PGRST116') {
        const { data: gastoActual } = await supabase
          .from('gastos')
          .select('estado_registro')
          .eq('id', gastoId)
          .eq('empresa_id', empresaActual.id)
          .maybeSingle()

        if (gastoActual?.estado_registro === 'anulado') {
          throw new Error(
            'Este gasto fue anulado mientras lo estabas editando.'
          )
        }

        throw new Error(
          'No tienes permisos para modificar este gasto.'
        )
      }

      throw errorActualizar
    }

    abrirModal({
      tipo: 'success',
      titulo: 'Gasto actualizado',
      mensaje: 'Los cambios fueron guardados correctamente.',
      onAceptar: () => {
        cerrarModal()
        navigate('/gastos', { state: { recargar: true } })
      },
    })
  }

  if (cargandoGasto) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando gasto...
          </p>
        </div>
      </div>
    )
  }

  if (!gasto) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => navigate('/gastos')}
          className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
        >
          <ArrowLeft size={18} />
          Volver a gastos
        </button>

        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <p className="text-slate-500">
            No fue posible mostrar este gasto.
          </p>
        </div>

        <ModalMensaje {...modal} />
      </div>
    )
  }

  return (
    <>
      <FormularioGasto
        modo="editar"
        gastoInicial={gasto}
        facturaVinculada={facturaVinculada}
        onGuardar={guardarEdicion}
        onCancelar={() => navigate('/gastos')}
      />

      <ModalMensaje {...modal} />
    </>
  )
}

export default EditarGasto
